import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useCallback, useLayoutEffect, useMemo, useRef} from 'react'

import type {CodeLineData} from '../components/blob/BlobContent/Code/hooks/use-code-lines'
import {isRowCollapsed} from '../utilities/lines'
import {useAddValueToLineElementMap, useGetValueFromLineElementMap} from './CurrentLineRefMap'
import type {SetStickyLinesType} from './use-sticky-lines'

// ---- Types ----

interface StartLineMetadata {
  type: 'start'
  codeLineData: CodeLineData
  numParents: number
  previousY: number
  previousRatio: number
}

interface EndLineMetadata {
  type: 'end'
  lineNumber: number
  previousY: number
  previousRatio: number
}

type LineMetadata = StartLineMetadata | EndLineMetadata

/**
 * Handle exposed by the shared observer for registering/unregistering elements.
 */
export interface SharedLineObserverHandle {
  observe(element: Element, codeLineData: CodeLineData, numParents: number): void
  unobserve(element: Element): void
}

// ---- Start / End observer logic (reused from original implementation) ----

function endObserverLogic(
  previousY: number,
  previousRatio: number,
  rect: DOMRectReadOnly,
  intersectionRatio: number,
  lineNumber: number,
  getStartLines: () => CodeLineData[] | undefined,
  isIntersecting: boolean,
  onLineStickOrUnstick: SetStickyLinesType,
) {
  // `rect` is the entry's `boundingClientRect`, already computed off the main
  // thread by the browser for this intersection record. Reusing it avoids a
  // synchronous `getBoundingClientRect()` layout flush inside the scroll-driven
  // observer callback, and keeps the rect consistent with `intersectionRatio`
  // (which also comes from the same entry).
  const currentY = rect.y
  const currentRatio = intersectionRatio
  const startLineArray = lineNumber ? getStartLines() : undefined
  const lineIsAboveBottomOfScreen = window.innerHeight - rect.bottom > 0
  const lineIsBelowTopOfScreen = rect.bottom > 0
  const lineIsCloseToTopOfScreen = rect.top < 150
  const lineIsOnScreen = lineIsAboveBottomOfScreen && lineIsBelowTopOfScreen
  const isNotNearBottomOfScreen = window.innerHeight - rect.bottom > 150 && window.innerHeight > 300

  for (const startLine of startLineArray || []) {
    if (startLine && lineIsOnScreen) {
      if (currentY < previousY && lineIsOnScreen) {
        if (currentRatio > previousRatio && isIntersecting) {
          // scrolling down and entering the screen — do nothing
        } else {
          // scrolling down and leaving the screen
          onLineStickOrUnstick(startLine, true)
        }
      } else if (currentY > previousY && isIntersecting) {
        if (currentRatio < previousRatio) {
          // scrolling up and leaving the screen — do nothing
        } else if (isNotNearBottomOfScreen && lineIsCloseToTopOfScreen) {
          // scrolling up and entering the screen
          onLineStickOrUnstick(startLine, false)
        }
      }
    }
  }

  return {currentY, currentRatio}
}

function startObserverLogic(
  codeLineData: CodeLineData,
  previousY: number,
  previousRatio: number,
  rect: DOMRectReadOnly,
  intersectionRatio: number,
  isIntersecting: boolean,
  onLineStickOrUnstick: SetStickyLinesType,
) {
  // See note in `endObserverLogic`: reuse the entry's pre-computed rect instead
  // of forcing a synchronous layout read in the observer callback.
  const currentY = rect.y
  const currentRatio = intersectionRatio
  const lineIsAboveBottomOfScreen = window.innerHeight - rect.bottom > 0
  const lineIsBelowTopOfScreen = rect.bottom > 0
  const lineIsCloseToTopOfScreen = rect.top < 150 && rect.top > -300
  const lineIsOnScreen = lineIsAboveBottomOfScreen && lineIsBelowTopOfScreen
  const lineIsNotDisplayed =
    rect.bottom === 0 && rect.top === 0 && rect.height === 0 && rect.width === 0 && rect.x === 0 && rect.y === 0

  if (!codeLineData.ownedSection || isRowCollapsed(codeLineData.ownedSection.startLine)) {
    return {currentY, currentRatio}
  }

  if (currentY <= previousY && (lineIsOnScreen || lineIsCloseToTopOfScreen) && !lineIsNotDisplayed) {
    if (currentRatio > previousRatio && isIntersecting) {
      // scrolling down and entering the screen — do nothing
    } else if (lineIsCloseToTopOfScreen) {
      // scrolling down and leaving the screen
      onLineStickOrUnstick(codeLineData, false)
    }
  } else if (currentY > previousY && isIntersecting) {
    if (currentRatio < previousRatio) {
      // scrolling up and leaving the screen — do nothing
    } else {
      // scrolling up and entering the screen
      onLineStickOrUnstick(codeLineData, true)
    }
  }

  return {currentY, currentRatio}
}

// ---- Observer factory helpers ----

const START_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]

function createStartObserver(
  stickyHeaderHeight: number,
  numParents: number,
  elementMetadata: Map<Element, LineMetadata>,
  onLineStickOrUnstickRef: React.RefObject<SetStickyLinesType>,
): IntersectionObserver {
  return new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const meta = elementMetadata.get(entry.target)
        if (!meta || meta.type !== 'start') continue

        const {currentY, currentRatio} = startObserverLogic(
          meta.codeLineData,
          meta.previousY,
          meta.previousRatio,
          entry.boundingClientRect,
          entry.intersectionRatio,
          entry.isIntersecting,
          onLineStickOrUnstickRef.current,
        )

        meta.previousY = currentY
        meta.previousRatio = currentRatio
      }
    },
    {
      root: null,
      rootMargin: `-${stickyHeaderHeight + numParents * 20}px 0px 0px 0px`,
      threshold: START_THRESHOLDS,
    },
  )
}

function createEndObserver(
  stickyHeaderHeight: number,
  elementMetadata: Map<Element, LineMetadata>,
  getValueFromMapRef: React.RefObject<(num: number) => CodeLineData[] | undefined>,
  onLineStickOrUnstickRef: React.RefObject<SetStickyLinesType>,
): IntersectionObserver {
  return new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const meta = elementMetadata.get(entry.target)
        if (!meta || meta.type !== 'end') continue

        const {currentY, currentRatio} = endObserverLogic(
          meta.previousY,
          meta.previousRatio,
          entry.boundingClientRect,
          entry.intersectionRatio,
          meta.lineNumber,
          () => getValueFromMapRef.current(meta.lineNumber),
          entry.isIntersecting,
          onLineStickOrUnstickRef.current,
        )

        meta.previousY = currentY
        meta.previousRatio = currentRatio
      }
    },
    {
      root: null,
      rootMargin: `-${stickyHeaderHeight}px 0px 0px 0px`,
      threshold: 0,
    },
  )
}

// ---- Main hook: creates the shared observers ----

/**
 * Creates a shared set of IntersectionObservers for sticky line tracking.
 * Instead of N observers (one per line), this creates ~7 total:
 * - 1 end observer (shared rootMargin for all end lines)
 * - ~5-6 start observers (one per unique numParents nesting depth)
 *
 * Call this once at the CodeBlob level and pass the handle down.
 */
export function useSharedLineObserver(
  canDoStickyLines: boolean,
  stickyHeaderHeight: number,
  onLineStickOrUnstick: SetStickyLinesType,
): SharedLineObserverHandle | undefined {
  const isSSR = useRuntimeEnvironment().isServer

  // Per-element metadata: tracks previousY/previousRatio and line info
  const elementMetadataRef = useRef(new Map<Element, LineMetadata>())

  // Observer instances
  const startObserversRef = useRef(new Map<number, IntersectionObserver>())
  const endObserverRef = useRef<IntersectionObserver | null>(null)

  // Stable refs for callbacks to avoid stale closures in observer callbacks
  const onLineStickOrUnstickRef = useRef(onLineStickOrUnstick)
  const addValueToMap = useAddValueToLineElementMap()
  const addValueToMapRef = useRef(addValueToMap)
  const getValueFromMap = useGetValueFromLineElementMap()
  const getValueFromMapRef = useRef(getValueFromMap)

  // Tracks which start lines have been registered in CurrentLineRefMap to avoid
  // duplicate registrations. Stored as a ref so it can be reset when
  // addValueToMap changes identity (soft navigation).
  const registeredStartLinesRef = useRef<Set<number>>(null)
  if (registeredStartLinesRef.current == null) {
    registeredStartLinesRef.current = new Set<number>()
  }

  // Ref for stickyHeaderHeight so observe() can lazily create observers with current value
  const stickyHeaderHeightRef = useRef(stickyHeaderHeight)

  // Guards whether the parent's observer-creation effect has run at least once.
  // On initial mount, children's useLayoutEffects fire first (React fires bottom-up).
  // Without this flag, children would lazy-create observers in observe(), only for
  // the parent's effect to immediately discard and recreate them.
  // With the flag, observe() defers observer creation on initial mount — it stores
  // metadata, and the parent's effect creates all observers from that metadata.
  const isInitializedRef = useRef(false)

  // Sync latest values to refs used by observer callbacks.
  // Done in useLayoutEffect (not during render) to satisfy React ref rules.
  // Within each component effects run in declaration order, so this fires
  // before the observer-recreation effect below.
  useLayoutEffect(() => {
    onLineStickOrUnstickRef.current = onLineStickOrUnstick
    getValueFromMapRef.current = getValueFromMap
    stickyHeaderHeightRef.current = stickyHeaderHeight

    // When addValueToMap changes identity (soft navigation — CurrentLineRefMapProvider
    // recreated its map), reset registeredStartLines so that the same line numbers
    // can be re-registered into the new map.
    if (addValueToMap !== addValueToMapRef.current) {
      registeredStartLinesRef.current = new Set<number>()
      addValueToMapRef.current = addValueToMap
    }
  })

  // Create/recreate all observers when stickyHeaderHeight changes.
  // On initial mount, children's useLayoutEffects fire first (bottom-up),
  // populating elementMetadataRef. This parent effect then creates observers
  // from that metadata and observes all registered elements in one pass.
  useLayoutEffect(() => {
    if (!canDoStickyLines || isSSR) return

    // Disconnect any existing observers before replacing them
    // to avoid leaked/duplicate observers.
    endObserverRef.current?.disconnect()
    for (const obs of startObserversRef.current.values()) {
      obs.disconnect()
    }

    const metadata = elementMetadataRef.current

    // Reset previousY/previousRatio for all tracked elements so that the
    // first callback from the new observers starts from the same clean state
    // that the legacy per-line implementation used when creating a fresh
    // IntersectionObserver (previousY = 0, previousRatio = 1).
    for (const meta of metadata.values()) {
      meta.previousY = 0
      meta.previousRatio = 1
    }

    // Create new end observer
    const newEndObserver = createEndObserver(stickyHeaderHeight, metadata, getValueFromMapRef, onLineStickOrUnstickRef)
    endObserverRef.current = newEndObserver

    // Create new start observers for each unique numParents currently tracked
    const newStartObservers = new Map<number, IntersectionObserver>()
    for (const meta of metadata.values()) {
      if (meta.type === 'start' && !newStartObservers.has(meta.numParents)) {
        newStartObservers.set(
          meta.numParents,
          createStartObserver(stickyHeaderHeight, meta.numParents, metadata, onLineStickOrUnstickRef),
        )
      }
    }
    startObserversRef.current = newStartObservers

    // Observe all registered elements with the new observers
    for (const [element, meta] of metadata) {
      if (meta.type === 'start') {
        newStartObservers.get(meta.numParents)?.observe(element)
      } else {
        newEndObserver.observe(element)
      }
    }

    isInitializedRef.current = true

    return () => {
      newEndObserver.disconnect()
      for (const obs of newStartObservers.values()) {
        obs.disconnect()
      }
    }
  }, [canDoStickyLines, isSSR, stickyHeaderHeight])

  // Stable handle — uses refs internally so it never needs to change identity.
  const handle = useMemo<SharedLineObserverHandle | undefined>(() => {
    if (isSSR) return undefined

    return {
      observe(element: Element, codeLineData: CodeLineData, numParents: number) {
        const {isStartLine, isEndLine, lineNumber, ownedSection} = codeLineData

        if (isStartLine) {
          const meta: StartLineMetadata = {
            type: 'start',
            codeLineData,
            numParents,
            previousY: 0,
            previousRatio: 1,
          }
          elementMetadataRef.current.set(element, meta)

          // Register the start→end mapping in CurrentLineRefMap (once per start line)
          if (ownedSection && lineNumber && !registeredStartLinesRef.current?.has(lineNumber)) {
            addValueToMapRef.current(ownedSection.endLine, {lineNumber})
            registeredStartLinesRef.current?.add(lineNumber)
          }

          // Before the parent's observer-creation effect has run (initial mount),
          // skip observer creation — the parent effect will create observers and
          // observe all elements in metadata. After initialization, lazy-create
          // for dynamically added numParents (e.g., virtualizer revealing new nesting depths).
          if (isInitializedRef.current) {
            let obs = startObserversRef.current.get(numParents)
            if (!obs) {
              obs = createStartObserver(
                stickyHeaderHeightRef.current,
                numParents,
                elementMetadataRef.current,
                onLineStickOrUnstickRef,
              )
              startObserversRef.current.set(numParents, obs)
            }
            obs.observe(element)
          }
        } else if (isEndLine && lineNumber) {
          const meta: EndLineMetadata = {
            type: 'end',
            lineNumber,
            previousY: 0,
            previousRatio: 1,
          }
          elementMetadataRef.current.set(element, meta)

          // After initialization, lazy-create end observer and observe immediately.
          // Before initialization (initial mount), the parent effect handles it.
          if (isInitializedRef.current) {
            if (!endObserverRef.current) {
              endObserverRef.current = createEndObserver(
                stickyHeaderHeightRef.current,
                elementMetadataRef.current,
                getValueFromMapRef,
                onLineStickOrUnstickRef,
              )
            }
            endObserverRef.current.observe(element)
          }
        }
      },

      unobserve(element: Element) {
        const meta = elementMetadataRef.current.get(element)
        if (!meta) return

        if (meta.type === 'start') {
          startObserversRef.current.get(meta.numParents)?.unobserve(element)
        } else {
          endObserverRef.current?.unobserve(element)
        }

        elementMetadataRef.current.delete(element)
      },
    }
  }, [isSSR])

  if (!canDoStickyLines || isSSR) return undefined

  return handle
}

// ---- Lightweight consumer hook ----

/**
 * Registers a code line element with the shared observer.
 * Returns a ref callback to attach to the line element.
 *
 * Replaces the per-line `useCodeLineIntersectionObservers` hook,
 * avoiding the creation of per-line IntersectionObserver instances.
 */
export function useRegisterLineObserver(
  sharedObserver: SharedLineObserverHandle | undefined,
  codeLineData: CodeLineData,
  numParents: number,
): (element: HTMLElement | null) => void {
  const elementRef = useRef<Element | null>(null)

  // Stable ref callback — just stores the element reference
  const refCallback = useCallback((element: HTMLElement | null) => {
    elementRef.current = element
  }, [])

  // Effect handles observation lifecycle: observe on mount/change, unobserve on cleanup.
  // Deps include codeLineData, numParents, and sharedObserver so re-observation
  // happens when line data changes (virtualizer slot reuse) or the handle changes
  // (soft navigation recreating the CurrentLineRefMap).
  useLayoutEffect(() => {
    const element = elementRef.current
    if (!element || !sharedObserver) return

    const {isStartLine, isEndLine} = codeLineData
    if (!isStartLine && !isEndLine) return

    sharedObserver.observe(element, codeLineData, numParents)

    return () => {
      sharedObserver.unobserve(element)
    }
  }, [codeLineData, numParents, sharedObserver])

  return refCallback
}
